import { ChevronLeft, ChevronRight } from "@tamagui/lucide-icons";
import { XStack } from "tamagui";

import type {
  ZocdocProviderResult,
  ZocdocProviderReviews,
  ZocdocTimeslot,
} from "@/api/generated/models";
import { Button, Heading, Paragraph, Skeleton } from "@/components";
import {
  addDaysYMD,
  formatDayHeading,
  formatDayName,
  todayYMD,
} from "@/utils/datetime";
import { PhaseContainer } from "./PhaseContainer";
import { ProviderCard } from "./ProvidersPhase";

type AvailabilityPhaseProps = {
  selectedProvider: ZocdocProviderResult;
  carrierName: string | null;
  reviews: ZocdocProviderReviews | null;
  timeslots: ZocdocTimeslot[];
  weekStartDate: string;
  isLoading: boolean;
  onChangeWeek: (weekStart: string) => void;
  onSelectDate: (date: string) => void;
  onBack: () => void;
};

export const AvailabilityPhase = ({
  selectedProvider,
  carrierName,
  reviews,
  timeslots,
  weekStartDate,
  isLoading,
  onChangeWeek,
  onSelectDate,
  onBack,
}: AvailabilityPhaseProps) => {
  const today = todayYMD();
  const days = Array.from({ length: 7 }, (_, i) =>
    addDaysYMD(weekStartDate, i),
  );
  const weekEndDate = days[6];
  const slotCounts: Record<string, number> = {};
  for (const slot of timeslots) {
    const date = slot.start_time.slice(0, 10);
    slotCounts[date] = (slotCounts[date] ?? 0) + 1;
  }
  const prevDisabled = weekStartDate <= today;

  return (
    <PhaseContainer currentStep={4} onBack={onBack}>
      <ProviderCard
        provider={selectedProvider}
        carrierName={carrierName}
        reviews={reviews}
      />
      <XStack justifyContent="space-between" alignItems="center">
        <Heading size="$sm">
          {formatDayHeading(weekStartDate, today)} -{" "}
          {formatDayHeading(weekEndDate, today)}
        </Heading>
        <XStack gap="$100" alignItems="center">
          <Button
            mode="tertiary"
            disabled={prevDisabled}
            opacity={prevDisabled ? 0.4 : 1}
            onPress={() => onChangeWeek(addDaysYMD(weekStartDate, -7))}
            icon={<ChevronLeft size={20} />}
            aria-label="Previous week"
            dataTrackingId="zocdoc-week-prev"
          />
          <Button
            mode="tertiary"
            onPress={() => onChangeWeek(addDaysYMD(weekStartDate, 7))}
            icon={<ChevronRight size={20} />}
            aria-label="Next week"
            dataTrackingId="zocdoc-week-next"
          />
        </XStack>
      </XStack>
      <XStack gap="$50">
        {days.map((date) => {
          if (isLoading) {
            return (
              <Skeleton
                key={date}
                flexGrow={1}
                flexBasis={0}
                minWidth={0}
                height={72}
                borderRadius="$100"
              />
            );
          }
          const count = slotCounts[date] ?? 0;
          const disabled = count === 0;
          return (
            <Button.Wrapper
              key={date}
              dataTrackingId="zocdoc-day-cell"
              onPress={() => onSelectDate(date)}
              disabled={disabled}
              pointerEvents={disabled ? "none" : "auto"}
              flexGrow={1}
              flexBasis={0}
              minWidth={0}
              height={72}
              backgroundColor="transparent"
              borderRadius="$100"
              borderWidth="$strokeWidth"
              borderColor="$strokeButtonSecondary"
              hoverStyle={{ backgroundColor: "$navHover" }}
              pressStyle={{ backgroundColor: "$navSelected" }}
              padding="$100"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="space-between"
              opacity={disabled ? 0.4 : 1}
            >
              <Paragraph size="$sm" color="$strokeButtonSecondary">
                {formatDayName(date)}
              </Paragraph>
              <Paragraph size="$xs" color="$strokeButtonSecondary">
                {count} appt{count === 1 ? "" : "s"}
              </Paragraph>
            </Button.Wrapper>
          );
        })}
      </XStack>
      {selectedProvider.statement && (
        <>
          <Heading size="$sm">About</Heading>
          <Paragraph size="$sm" color="$textSecondary">
            {selectedProvider.statement}
          </Paragraph>
        </>
      )}
    </PhaseContainer>
  );
};
